// Ticket thread helpers (non-component) shared by the admin inbox and the
// client Support view: id/name formatting, thread assembly and message POST.
import { api } from "../lib/api";
import { fmtSize, type TicketDetail, type VTicket } from "./data";

export const id8 = (id: string) => id.slice(0, 8);
export const firstName = (name: string) => name.split(/[\s@]+/)[0] || name;

export function initials(name: string): string {
  if (name.includes("@")) return name.slice(0, 2).toUpperCase();
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "–"
  );
}

/** "09:12" today, else "8 Oct, 09:12". */
export function threadTime(ms: number): string {
  const d = new Date(ms);
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${time}`;
}

export interface ThreadFile {
  id: string;
  name: string;
  size: string;
  url: string;
  preview: string | null; // inline image URL, raster images only
}

export interface ThreadItem {
  key: string;
  kind: "msg" | "note" | "event";
  side: "team" | "client";
  who: string;
  at: number;
  body: string;
  files: ThreadFile[];
}

const PREVIEW_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/** The ticket body, its comments (with files) and history, oldest first. */
export function buildThread(t: VTicket, detail: TicketDetail | null): ThreadItem[] {
  const items: ThreadItem[] = [];
  if (t.body.trim())
    items.push({
      key: "body",
      kind: "msg",
      side: t.authorSide,
      who: t.author,
      at: t.opened,
      body: t.body,
      files: [],
    });
  const base = `/api/projects/${t.projectId}/tickets/${t.id}/files`;
  for (const c of detail?.comments ?? [])
    items.push({
      key: c.id,
      kind: c.internal ? "note" : "msg",
      side: c.side,
      who: c.author_name || c.author_email || (c.side === "team" ? "Agile Software" : "Client"),
      at: c.created_at,
      body: c.body,
      files: (c.files ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        size: fmtSize(f.size),
        url: `${base}/${f.id}`,
        preview: PREVIEW_TYPES.includes(f.content_type) ? `${base}/${f.id}?inline=1` : null,
      })),
    });
  for (const h of detail?.history ?? [])
    items.push({ key: h.id, kind: "event", side: "team", who: "", at: h.at, body: h.text, files: [] });
  // Chronological; at equal times an event ("Raised by …") leads.
  return items.sort(
    (a, b) => a.at - b.at || (a.kind === "event" ? -1 : 0) - (b.kind === "event" ? -1 : 0),
  );
}

export interface ThreadSend {
  body: string;
  note: boolean;
  wait: boolean;
  files: File[];
}

/** POST a thread message (multipart when it carries files). */
export async function postTicketMessage(
  t: VTicket,
  m: ThreadSend,
): Promise<{ boardStatus: string | null; assignee: string | null }> {
  const path = `/projects/${t.projectId}/tickets/${t.id}/comments`;
  if (!m.files.length) {
    return api.post(path, { body: m.body, internal: m.note, wait: m.wait });
  }
  const form = new FormData();
  form.append("body", m.body);
  form.append("internal", String(m.note));
  form.append("wait", String(m.wait));
  for (const f of m.files) form.append("files", f, f.name);
  return api.upload(path, form);
}
